import React, { useState, useEffect } from 'react';
import axios from '../api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  Typography,
  Divider,
  Card,
  InputAdornment,
  IconButton
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { useToast } from './ToastProvider';

const EditCustomerForm = ({ customer, onUpdate, closeModal }) => {
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    ph1: '',
    ph2: '',
    address: '',
    email: '',
    place: '',
  });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const showToast = useToast();

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        company_name: customer.company_name || '',
        ph1: customer.ph1 || '',
        ph2: customer.ph2 || '',
        address: customer.address || '',
        email: customer.email || '',
        place: customer.place || '',
      });
    }
  }, [customer]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    try {
      await axios.put(`/api/customer/${customer.id}`, formData);
      onUpdate();
      closeModal();
      showToast('Customer updated successfully', 'success');
    } catch (error) {
      let msg = 'Error updating customer.';
      setErrorMsg(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const modalPaperSx = {
    borderRadius: 2,
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
    bgcolor: 'background.paper',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
  };
  const dialogTitleSx = {
    fontSize: { xs: '1rem', sm: '1.125rem' },
    fontWeight: 600,
    color: 'text.primary',
    px: { xs: 2, sm: 3 },
    py: { xs: 1.5, sm: 2 },
    borderBottom: '1px solid',
    borderColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };
  const labelSx = { fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', mb: 0.5, display: 'block' };
  const sectionTitleSx = { fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', mb: 1.5, display: 'block' };
  const inputFieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 1.25, fontSize: '0.875rem', '& fieldset': { borderColor: 'divider' } } };

  return (
    <Dialog
      open
      onClose={closeModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
      sx={{ '& .MuiDialog-paper': { mx: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}
    >
      <DialogTitle sx={dialogTitleSx}>
        <span>Edit Customer</span>
        <IconButton size="small" onClick={closeModal} sx={{ color: 'text.secondary' }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit} id="edit-customer-form">
        <DialogContent sx={{ overflow: 'auto', flex: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
          {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
          <Typography sx={sectionTitleSx}>Personal Information</Typography>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Full Name *</Typography>
              <TextField
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                placeholder="Full name"
                InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon color="action" sx={{ fontSize: 18 }} /></InputAdornment> }}
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Company Name</Typography>
              <TextField
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="Company name"
                InputProps={{ startAdornment: <InputAdornment position="start"><BusinessIcon color="action" sx={{ fontSize: 18 }} /></InputAdornment> }}
                sx={inputFieldSx}
              />
            </Grid>
          </Grid>
          <Typography sx={sectionTitleSx}>Contact Information</Typography>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Phone 1 *</Typography>
              <TextField
                name="ph1"
                value={formData.ph1}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                placeholder="Phone 1"
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon color="action" sx={{ fontSize: 18 }} /></InputAdornment> }}
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Phone 2 (Optional)</Typography>
              <TextField
                name="ph2"
                value={formData.ph2}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="Phone 2"
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon color="action" sx={{ fontSize: 18 }} /></InputAdornment> }}
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={labelSx}>Email Address</Typography>
              <TextField
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="Email"
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon color="action" sx={{ fontSize: 18 }} /></InputAdornment> }}
                sx={inputFieldSx}
              />
            </Grid>
          </Grid>
          <Typography sx={sectionTitleSx}>Address Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Place</Typography>
              <TextField
                name="place"
                value={formData.place}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="Place"
                InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon color="action" sx={{ fontSize: 18 }} /></InputAdornment> }}
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Full Address *</Typography>
              <TextField
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                multiline
                minRows={2}
                placeholder="Full address"
                InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}><HomeIcon color="action" sx={{ fontSize: 18 }} /></InputAdornment> }}
                sx={inputFieldSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button variant="outlined" onClick={closeModal} disabled={loading} sx={{ textTransform: 'none', fontWeight: 500 }}>Cancel</Button>
          <Button type="submit" form="edit-customer-form" variant="contained" color="primary" disabled={loading} sx={{ textTransform: 'none', fontWeight: 500 }}>{loading ? 'Updating...' : 'Update Customer'}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default EditCustomerForm;
